import {gql, useMutation} from '@apollo/client';
import {Pda} from './components/Pda';
import {Header} from './components/Header';
import { ArrivalRelatedScanComponent, HandleScanEventResult } from './components/ArrivalRelatedScanWeightComponent';
import { showTrackingNumberError } from '../../utils/BarCodeUtil';
import { isTrackingNumber } from '../../utils/BarCodeUtil';
import {SettingOutlined} from "@ant-design/icons";
import React, { useState } from "react";
import { t } from '../../i18n';
import { getNavBarContainerStyle } from '../../utils/deviceUtils';
import {NavBar, Switch, Popup, Dialog} from "antd-mobile";
import {ArrowLeftOutlined} from "@ant-design/icons";
import BluetoothScaleSelector from './components/BluetoothScaleSelector';
import { useBluetoothScaleStore } from '../../store/bluetoothScaleStore';

const HANDLE_WEIGHT_PARCEL_SCAN_EVENT = gql`
   mutation HandleWeightParcelScanEvent($barCode: String!, $weightKg: Float!) {
     success: handleWeightParcelScanEvent(barCode: $barCode, weightKg: $weightKg)
   }
`

interface PageBodyProps {
    showSettings: boolean;
    setShowSettings: (show: boolean) => void;
}

const PageBody = ({ showSettings, setShowSettings }: PageBodyProps) => {
    const [handleWeightParcelScanEvent] = useMutation<HandleScanEventResult>(HANDLE_WEIGHT_PARCEL_SCAN_EVENT);

    // 使用全局蓝牙状态
    const { isConnected: isBluetoothConnected, currentWeightData } = useBluetoothScaleStore();

    const handleScanEvent = async (barCode: string): Promise<HandleScanEventResult> => {
        if (!isTrackingNumber(barCode)) {
            showTrackingNumberError(barCode);
            return {success: false};
        }

        // 称重扫描必须有蓝牙连接和重量数据
        if (!isBluetoothConnected) {
            // 弹窗提示未连接蓝牙，不抛出异常
            Dialog.alert({
                content: t('pda.pages.weightParcel.bluetoothNotConnected'),
                confirmText: t('pda.common.confirm'),
            });
            return {success: false};
        }
        if (!currentWeightData || currentWeightData.weight <= 0) {
            // 弹窗提示无法获取重量
            Dialog.alert({
                content: t('pda.pages.weightParcel.weightNotAvailable'),
                confirmText: t('pda.common.confirm'),
            });
            return {success: false};
        }

        // 检查 rawData 的第一个字符是否为负号
        if (currentWeightData.rawData && currentWeightData.rawData.charAt(0) === '-') {
            // 弹窗提示重量为负数
            Dialog.alert({
                content: t('pda.pages.weightParcel.negativeWeightError'),
                confirmText: t('pda.common.confirm'),
            });
            return {success: false};
        }

        // 单位转换：lb转kg，kg保持不变
        let weightKg = currentWeightData.unit.toLowerCase() === 'lb'
            ? currentWeightData.weight * 0.453592
            : currentWeightData.weight;

        // 保留3位小数
        weightKg = Math.round(weightKg * 1000) / 1000;

        const {data} = await handleWeightParcelScanEvent({
            variables: {
                barCode,
                weightKg: weightKg
            }
        });
        return {success: data?.success || false};
    };

    return (
        <>
            {/* 设置弹出层 */}
            <Popup
                visible={showSettings}
                onMaskClick={() => setShowSettings(false)}
                position="right"
                bodyStyle={{ width: '100vw', height: '100vh', padding: 0 }}
            >
                <div className="w-full h-full flex flex-col">
                    {/* 设置标题栏 */}
                    <div className="flex items-center px-4 py-3 bg-white shadow-sm">
                        <div
                            className="cursor-pointer"
                            onClick={() => setShowSettings(false)}
                        >
                            <ArrowLeftOutlined className="text-xl" />
                        </div>
                        <h2 className="flex-1 text-center text-lg font-semibold">{t('pda.pages.weightParcel.settings')}</h2>
                        <div className="w-6"></div> {/* 占位平衡 */}
                    </div>

                    {/* 设置内容 */}
                    <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
                        {/* 蓝牙电子秤组件 */}
                        <BluetoothScaleSelector />
                    </div>
                </div>
            </Popup>

            {/* 扫描组件 */}
            <ArrivalRelatedScanComponent
                value1Placeholder={t('pda.pages.weightParcel.trackingNO')}
                value1ColumnTitle={t('pda.pages.weightParcel.trackingNO')}
                handleScanEvent={handleScanEvent}
                weighSimultaneously={true}
                currentWeight={currentWeightData}
                isBluetoothConnected={isBluetoothConnected}
                successExtraFields={[{ title: t('pda.pages.weightParcel.weight'), key: 'weight' }]}
            />
        </>
    );
}

export default function WeightParcelPage() {
    const [showSettings, setShowSettings] = useState(false);

    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            {/* 顶部导航栏 */}
            <div
                {...getNavBarContainerStyle('relative')}
                style={{ ...(getNavBarContainerStyle().style || {}), paddingTop: 0 }}
            >
                <Header title={t('pda.pages.weightParcel.weightParcelScan')}/>
                <div
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center cursor-pointer"
                    onClick={() => setShowSettings(true)}
                >
                    <SettingOutlined className="text-xl text-gray-600" />
                </div>
            </div>
            <Pda/>
            <PageBody showSettings={showSettings} setShowSettings={setShowSettings} />
        </div>
    );
}

